import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { DatasetService } from "../../../services/DatasetService";
import { CompositionService } from "../../../services/CompositionService";
import CompositionBaseType from "./Steps/CompositionBaseType";
import CompositionDetails from "./Steps/CompositionDetails";
import CompositionBuild from "./Steps/CompositionBuild";
import CompositionReview from "./Steps/CompositionReview";
import { SelectedDataset, Dataset, calculateSampledRequestCount } from "./CompositionDetail";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  FileTextIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification } from "antd";

export const NEW_COMPOSITION_STORAGE_KEY = "compositionData";

interface CompositionData {
  name: string;
  description: string;
  baseType?: "intent_group" | "application_intent";
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
  datasets: Array<{
    id: number;
    percentage: number;
    sampleType: "request" | "session";
    samplePercentage: number;
  }>;
}

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const CompositionNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  const [availableDatasets, setAvailableDatasets] = useState<Dataset[]>([]);
  const [selectedDatasets, setSelectedDatasets] = useState<SelectedDataset[]>([]);
  const [totalPercentage, setTotalPercentage] = useState<number>(0);

  // Dataset configuration modal state
  const [configModalVisible, setConfigModalVisible] = useState<boolean>(false);
  const [currentDataset, setCurrentDataset] = useState<SelectedDataset | null>(null);
  const [configSampleType, setConfigSampleType] = useState<"request" | "session">("request");
  const [configSamplePercentage, setConfigSamplePercentage] = useState<number>(100);

  const [compositionData, setCompositionData] = useState<CompositionData>(() => {
    const saved = localStorage.getItem(NEW_COMPOSITION_STORAGE_KEY);
    
    // Get URL parameters for prepopulation
    const baseType = searchParams.get("base_type") as "intent_group" | "application_intent" | undefined;
    const intentGroupId = searchParams.get("intent_group_id");
    const applicationId = searchParams.get("application_id");
    const intentId = searchParams.get("intent_id");
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          baseType: baseType || parsed.baseType,
          intentGroupId: intentGroupId ? parseInt(intentGroupId) : parsed.intentGroupId,
          applicationId: applicationId ? parseInt(applicationId) : parsed.applicationId,
          intentId: intentId ? parseInt(intentId) : parsed.intentId,
        };
      } catch (e) {
        console.error("Error parsing saved composition data", e);
      }
    }

    return {
      name: "",
      description: "",
      baseType: baseType,
      intentGroupId: intentGroupId ? parseInt(intentGroupId) : undefined,
      applicationId: applicationId ? parseInt(applicationId) : undefined,
      intentId: intentId ? parseInt(intentId) : undefined,
      datasets: []
    };
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_COMPOSITION_STORAGE_KEY, JSON.stringify(compositionData));
  }, [compositionData]);

  // Update URL when step changes
  useEffect(() => {
    // Only update URL if step parameter should be present (when step > 0 or step was already in URL initially)
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...currentParams,
        step: currentStep.toString()
      }, { replace: true }); // Use replace to avoid creating new history entries for step changes
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Fetch available datasets
  useEffect(() => {
    fetchDatasets();
  }, [apiKey, compositionData.baseType, compositionData.intentGroupId, compositionData.applicationId, compositionData.intentId]);

  const isDatasetUsedInTest = (dataset: Dataset): boolean =>
    (dataset as any)?.meta?.used_in_test === true;

  const fetchDatasets = () => {
    if (compositionData.baseType === "intent_group" && compositionData.intentGroupId) {
      // Fetch datasets filtered by intent group
      DatasetService.getDatasetsByIntentGroup(
        compositionData.intentGroupId,
        0,
        100,
        setDatasetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          const items = response.items || response || [];
          setAvailableDatasets(items.filter((d: any) => !isDatasetUsedInTest(d)));
        },
        (error: any) => {
          console.error("Error fetching datasets by intent group:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    } else if (compositionData.baseType === "application_intent" && 
               compositionData.applicationId && 
               compositionData.intentId) {
      // Fetch datasets filtered by application/intent
      DatasetService.getDatasetsByIntent(
        compositionData.applicationId,
        compositionData.intentId,
        0,
        100,
        setDatasetsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          const items = response.items || response || [];
          setAvailableDatasets(items.filter((d: any) => !isDatasetUsedInTest(d)));
        },
        (error: any) => {
          console.error("Error fetching datasets by application/intent:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    } else {
      // Fetch all datasets if no base type is selected or if selection is incomplete
      DatasetService.getDatasets(0, 100, setDatasetsLoading, apiKey).subscribe(
        (response: any) => {
          const items = response.items || [];
          setAvailableDatasets(items.filter((d: any) => !isDatasetUsedInTest(d)));
        },
        (error: any) => {
          console.error("Error fetching datasets:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch datasets: ${error}`,
          });
        }
      );
    }
  };

  // Update composition data when form values change
  const handleFormValuesChange = (changedValues: any) => {
    setCompositionData((prevData: CompositionData) => ({
      ...prevData,
      ...changedValues,
    }));
  };

  // Add dataset to composition
  const handleAddDataset = (dataset: Dataset) => {
    try {
      if (isDatasetUsedInTest(dataset)) {
        notification.warning({
          message: "Dataset restricted",
          description:
            "This dataset is marked as 'Used In Test' and cannot be used in a composition.",
        });
        return;
      }
      // Check if dataset is already in composition
      if (selectedDatasets.some((item) => item.dataset.id === dataset.id)) {
        notification.warning({
          message: "Dataset already added",
          description: "This dataset is already part of your composition.",
        });
        return;
      }

      // Add new dataset with default sampling settings
      const newSelectedDatasets: SelectedDataset[] = [...selectedDatasets, { 
        dataset, 
        percentage: 0, 
        sampleType: "request" as const, 
        samplePercentage: 100 
      }];
      
      // Recalculate percentages based on sampled request counts
      const updatedDatasets = recalculatePercentagesBasedOnSampling(newSelectedDatasets);
      
      setSelectedDatasets(updatedDatasets);
      updateTotalPercentage(updatedDatasets);
      
      // Update composition data
      setCompositionData((prevData: CompositionData) => {
        return {
          ...prevData,
          datasets: updatedDatasets.map(item => ({
            id: item.dataset.id,
            percentage: item.percentage,
            sampleType: item.sampleType,
            samplePercentage: item.samplePercentage,
          })),
        };
      });
    } catch (error) {
      console.error("Error in handleAddDataset:", error);
    }
  };

  // Remove dataset from composition
  const handleRemoveDataset = (datasetId: number) => {
    const updatedDatasets = selectedDatasets.filter(
      (item) => item.dataset.id !== datasetId
    );

    if (updatedDatasets.length > 0) {
      // Recalculate percentages based on sampled request counts
      const recalculatedDatasets = recalculatePercentagesBasedOnSampling(updatedDatasets);
      
      setSelectedDatasets(recalculatedDatasets);
      updateTotalPercentage(recalculatedDatasets);
      
      // Update composition data
      setCompositionData((prevData: CompositionData) => {
        return {
          ...prevData,
          datasets: recalculatedDatasets.map(item => ({
            id: item.dataset.id,
            percentage: item.percentage,
            sampleType: item.sampleType,
            samplePercentage: item.samplePercentage,
          })),
        };
      });
    } else {
      setSelectedDatasets([]);
      updateTotalPercentage([]);
      
      // Update composition data
      setCompositionData((prevData: CompositionData) => {
        return {
          ...prevData,
          datasets: [],
        };
      });
    }
  };

  // Calculate total percentage
  const updateTotalPercentage = (datasets: SelectedDataset[]) => {
    const total = datasets.reduce((sum, item) => sum + item.percentage, 0);
    setTotalPercentage(total);
  };

  // Recalculate percentages based on sampled request counts
  const recalculatePercentagesBasedOnSampling = (datasets: SelectedDataset[]): SelectedDataset[] => {
    if (datasets.length === 0) return [];
    
    // Calculate sampled request counts
    const totalSampledRequests = datasets.reduce(
      (sum, item) => {
        const sampledCount = calculateSampledRequestCount(item.dataset, item.samplePercentage);
        return sum + sampledCount;
      }, 
      0
    );
    
    if (totalSampledRequests === 0) return datasets;
    
    // Calculate new percentages based on sampled counts
    const updatedDatasets = datasets.map(item => {
      const sampledCount = calculateSampledRequestCount(item.dataset, item.samplePercentage);
      const newPercentage = Math.round((sampledCount / totalSampledRequests) * 100);
      return {
        ...item,
        percentage: newPercentage
      };
    });
    
    // Adjust to ensure total is exactly 100%
    const currentTotal = updatedDatasets.reduce((sum, item) => sum + item.percentage, 0);
    if (currentTotal !== 100 && updatedDatasets.length > 0) {
      // Add or subtract the difference from the dataset with the most sampled requests
      const sortedByRequests = [...updatedDatasets].sort((a, b) => 
        calculateSampledRequestCount(b.dataset, b.samplePercentage) -
        calculateSampledRequestCount(a.dataset, a.samplePercentage)
      );
      sortedByRequests[0].percentage += (100 - currentTotal);
    }
    
    return updatedDatasets;
  };

  // Handle dataset configuration
  const handleConfigureDataset = (dataset: SelectedDataset) => {
    setCurrentDataset(dataset);
    setConfigSampleType(dataset.sampleType);
    setConfigSamplePercentage(dataset.samplePercentage);
    setConfigModalVisible(true);
  };

  // Save dataset configuration
  const handleSaveConfig = () => {
    if (!currentDataset) return;
    
    // Update the dataset in the selectedDatasets array
    let updatedDatasets = selectedDatasets.map(item => 
      item.dataset.id === currentDataset.dataset.id 
        ? { ...item, sampleType: configSampleType, samplePercentage: configSamplePercentage }
        : item
    );
    
    // Recalculate percentages based on sampled request counts
    updatedDatasets = recalculatePercentagesBasedOnSampling(updatedDatasets);
    
    setSelectedDatasets(updatedDatasets);
    updateTotalPercentage(updatedDatasets);
    
    // Update composition data
    setCompositionData((prevData: CompositionData) => {
      return {
        ...prevData,
        datasets: updatedDatasets.map(item => ({
          id: item.dataset.id,
          percentage: item.percentage,
          sampleType: item.sampleType,
          samplePercentage: item.samplePercentage,
        })),
      };
    });
    
    setConfigModalVisible(false);
    setCurrentDataset(null);
  };

  // Steps components
  const steps: StepItem[] = [
    {
      key: "base-type",
      title: "Base Type",
      component: (
        <CompositionBaseType
          baseType={compositionData.baseType}
          intentGroupId={compositionData.intentGroupId}
          applicationId={compositionData.applicationId}
          intentId={compositionData.intentId}
          onChange={(data) => {
            // Clear selected datasets when base type changes
            if (data.baseType !== compositionData.baseType) {
              setSelectedDatasets([]);
              updateTotalPercentage([]);
            }
            
            setCompositionData((prevData) => ({
              ...prevData,
              baseType: data.baseType || prevData.baseType,
              intentGroupId: data.intentGroupId,
              applicationId: data.applicationId,
              intentId: data.intentId,
              // Clear datasets if base type changes
              datasets: data.baseType !== prevData.baseType ? [] : prevData.datasets
            }));
          }}
        />
      ),
    },
    {
      key: "details",
      title: "Details",
      component: (
        <CompositionDetails
          name={compositionData.name}
          description={compositionData.description}
          onChange={handleFormValuesChange}
        />
      ),
    },
    {
      key: "build",
      title: "Build",
      component: (
        <CompositionBuild
          availableDatasets={availableDatasets}
          selectedDatasets={selectedDatasets}
          datasetsLoading={datasetsLoading}
          onAddDataset={handleAddDataset}
          onRemoveDataset={handleRemoveDataset}
          onConfigureDataset={handleConfigureDataset}
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: (
        <CompositionReview
          name={compositionData.name}
          description={compositionData.description}
          selectedDatasets={selectedDatasets}
          onConfigureDataset={handleConfigureDataset}
          onEditStep={() => setCurrentStep(2)} // Go back to Build step
        />
      ),
    },
  ];

  const canProceed = () => {
    if (currentStep === 0) {
      // Base Type step validation
      if (compositionData.baseType === "intent_group") {
        return !!compositionData.intentGroupId;
      } else if (compositionData.baseType === "application_intent") {
        return !!compositionData.applicationId && !!compositionData.intentId;
      }
      return !!compositionData.baseType;
    }
    if (currentStep === 1) {
      return !!compositionData.name && !!compositionData.description && compositionData.description.trim().length > 0;
    }
    if (currentStep === 2) {
      return selectedDatasets.length > 0 && totalPercentage === 100;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createComposition = async () => {
    setLoading(true);
    
    const payload = {
      name: compositionData.name,
      description: compositionData.description,
      base_type: compositionData.baseType,
      intent_group_id: compositionData.baseType === "intent_group" ? compositionData.intentGroupId : undefined,
      application_id: compositionData.baseType === "application_intent" ? compositionData.applicationId : undefined,
      intent_id: compositionData.baseType === "application_intent" ? compositionData.intentId : undefined,
      datasets: selectedDatasets.map(item => ({
        dataset_id: item.dataset.id,
        percentage: item.percentage,
        sample_type: item.sampleType,
        sample_percentage: item.samplePercentage
      }))
    };
    
    CompositionService.createComposition(payload, setLoading, apiKey).subscribe(
      (response: any) => {
        notification.success({
          message: "Composition created successfully",
          description: "Your composition has been created successfully",
        });
        localStorage.removeItem(NEW_COMPOSITION_STORAGE_KEY);
        navigate("/forge/compositions");
      },
      (error: any) => {
        console.error("Error creating composition:", error);
        notification.error({
          message: "Error",
          description: `Failed to create composition: ${error}`,
        });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    // Check if each previous step would be valid
    if (index === 0) {
      if (compositionData.baseType === "intent_group") {
        return !!compositionData.intentGroupId;
      } else if (compositionData.baseType === "application_intent") {
        return !!compositionData.applicationId && !!compositionData.intentId;
      }
      return !!compositionData.baseType;
    }
    if (index === 1) {
      return !!compositionData.name && !!compositionData.description && compositionData.description.trim().length > 0;
    }
    if (index === 2) {
      return selectedDatasets.length > 0 && totalPercentage === 100;
    }
    return true;
  }).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/forge/compositions")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Compositions
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">New Composition</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Composition
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new composition by combining multiple datasets with custom sampling
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep
                            ? "bg-maitai-green"
                            : index === currentStep
                            ? "bg-maitai-green/60"
                            : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep
                        ? "text-maitai-green"
                        : index < currentStep
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep
                        ? "bg-maitai-green text-white"
                        : index < currentStep
                        ? "bg-maitai-green text-white"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? (
                        <CheckIcon className="w-3 h-3" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading}
                    onClick={createComposition}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating composition..." : (
                      <>
                        <FileTextIcon className="w-4 h-4" />
                        Create Composition
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dataset Configuration Modal */}
      <Dialog open={configModalVisible} onOpenChange={setConfigModalVisible}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configure Dataset Sampling</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sampleType">Sample Type</Label>
              <Select value={configSampleType} onValueChange={(value: "request" | "session") => setConfigSampleType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sample type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="request">Request</SelectItem>
                  <SelectItem value="session">Session</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="samplePercentage">Sample Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="samplePercentage"
                  type="number"
                  min={1}
                  max={100}
                  value={configSamplePercentage}
                  onChange={(e) => setConfigSamplePercentage(Number(e.target.value))}
                  placeholder="Enter percentage"
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
            {currentDataset && (
              <div className="space-y-2 pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Dataset: <span className="font-gc-med text-foreground">{currentDataset.dataset.name}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Requests: <span className="font-gc-med text-foreground">{currentDataset.dataset.request_count.toLocaleString()}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  After sampling: <span className="font-gc-med text-foreground">
                    {Math.round(currentDataset.dataset.request_count * (configSamplePercentage / 100)).toLocaleString()}
                    {configSampleType === "session" ? "*" : ""}
                  </span> requests
                  {configSampleType === "session" && (
                    <div className="text-xs text-muted-foreground mt-1">
                      * Estimated count for session-based sampling
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigModalVisible(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompositionNew; 